/**
 * Price Action: уровни и форма свечей.
 *
 * Здесь нет индикаторов в привычном смысле — только сама цена: где она
 * была, куда вернулась и как закрылась свеча.
 */
import { above, breakOut, close, ema, happened, ind, inZone, mirror, number, recently, rvol, spec, swingParams, weight, } from './kit.js';
export const PRICE_ACTION_TEMPLATES = [
    {
        id: 'level-retest',
        name: 'Возврат к уровню',
        icon: '🎯',
        category: 'price-action',
        popular: true,
        hint: 'Цена вернулась к прошлому экстремуму и оттолкнулась — уровень удержал.',
        steps: ['Крупный тренд по нужную сторону', 'Цена вернулась к экстремуму', 'Свеча отказа и объём'],
        keywords: ['уровень', 'поддержка', 'сопротивление', 'retest'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Прошлый минимум — это уровень поддержки. В растущем рынке цена возвращается к нему и отталкивается.',
            why: 'На уровнях копятся заявки: кто не успел купить в прошлый раз, ставит лимитки туда же.',
            fails: 'Когда уровень проверяют слишком часто — заявки на нём выбирают, и следующий удар его пробивает.',
            tip: 'Первое касание уровня надёжнее третьего.',
        },
        build: (o) => spec(o, 'Возврат к уровню', mirror((side) => ({
            context: [side.beyond(close, ema(200))],
            trigger: side.cross(close, ind('swing', swingParams, side.liquidity === 'low' ? 'low' : 'high')),
            confirmations: [weight('c1', happened(ind('pin_bar', { factor: 2 }, side.way)), 50), weight('c2', above(rvol, number(1.2)), 50)],
        })), 0),
    },
    {
        id: 'pin-bar',
        name: 'Пин-бар',
        icon: '🕯',
        category: 'price-action',
        hint: 'Длинная тень: цену завели за уровень и вернули внутри одной свечи.',
        steps: ['Пин-бар в нашу сторону', 'Цена у края диапазона', 'Объём'],
        keywords: ['пин-бар', 'pin bar', 'молот', 'свеча'],
        passport: {
            level: 'novice',
            market: 'range',
            horizon: 'swing',
            style: 'counter-trend',
            timeframe: '4H',
            idea: 'Свеча с маленьким телом и длинной тенью: цену пытались увести, но за одну свечу её вернули.',
            why: 'Тень — отказ рынка идти дальше. У края диапазона такой отказ часто начинает разворот.',
            fails: 'В середине диапазона пин-бары — шум; поэтому край здесь — главный усилитель.',
            tip: 'Защита — за кончиком тени: риск маленький и понятный.',
        },
        build: (o) => spec(o, 'Пин-бар', mirror((side) => ({
            context: [],
            trigger: happened(ind('pin_bar', { factor: 2 }, side.way)),
            confirmations: [weight('c1', inZone(side, 40), 60), weight('c2', above(rvol, number(1.2)), 40)],
        })), 50),
    },
    {
        id: 'inside-bar-break',
        name: 'Пробой внутреннего бара',
        icon: '📦',
        category: 'price-action',
        hint: 'Свеча сжалась внутри предыдущей, следующая вышла за её границу.',
        steps: ['Был внутренний бар', 'Пробита граница последних трёх баров', 'Объём'],
        keywords: ['inside bar', 'внутренний бар', 'сжатие'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '4H',
            idea: 'Внутренний бар — свеча внутри предыдущей: рынок на мгновение замер. Выход из неё показывает сторону.',
            why: 'Короткое равновесие перед движением: у его границ плотно стоят заявки.',
            fails: 'На младших таймфреймах внутренних баров слишком много, и выходы из них — шум.',
            tip: 'Надёжнее по направлению тренда: против тренда выход часто ложный.',
        },
        build: (o) => spec(o, 'Пробой внутреннего бара', mirror((side) => ({
            context: [recently(ind('inside_bar', {}), 2)],
            trigger: breakOut(side, 3),
            confirmations: [weight('c1', above(rvol, number(1.2)), 60), weight('c2', side.beyond(close, ema(50)), 40)],
        })), 0),
    },
    {
        id: 'swing-reversal',
        name: 'Разворот на экстремуме',
        icon: '🪃',
        category: 'price-action',
        hint: 'Ликвидность за экстремумом снята, и следом пришло поглощение.',
        steps: ['Ликвидность снята', 'Поглощение в нашу сторону', 'Объём'],
        keywords: ['разворот', 'swing', 'экстремум'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '15m',
            idea: 'Цена кольнула за экстремум, собрала стопы, а следующая свеча поглотила движение в обратную сторону.',
            why: 'Два признака разворота подряд — сбор стопов и перехват инициативы — вместе надёжнее каждого.',
            fails: 'Если поглощение маленькое, это просто колебание после снятия.',
            tip: 'Чем больше объём на поглощении, тем увереннее разворот.',
        },
        build: (o) => spec(o, 'Разворот на экстремуме', mirror((side) => ({
            context: [recently(ind('sweep', swingParams, side.liquidity), 5)],
            trigger: happened(ind('engulfing', { factor: 1 }, side.way)),
            confirmations: [weight('c1', above(rvol, number(1.3)), 100)],
        })), 0),
    },
];
//# sourceMappingURL=price-action.js.map